import type { PartiallyOptional } from '@cdc-internal/fe-toolbox/type-utils'
import { Avatar, Box, Skeleton } from '@mantine/core'
import NextImage, { type ImageProps as NextImageProps } from 'next/image'
import { useState } from 'react'

const defaultFallbackBg = 'var(--bg-surface-emphasis)'
// Generated from https://png-pixel.com/ with --bg-surface-emphasis color and 0.9 opacity
const defaultPlaceholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mM0sgl6CgADDwGnZLteDwAAAABJRU5ErkJggg=='

export interface NextBaseIconProps extends PartiallyOptional<NextImageProps, 'src'> {
  // Indicate loading state if src is not ready yet
  sourcePending?: boolean
  // If true, the icon and skeleton will be a circle
  circle?: boolean
  // Square size in equal width and height
  size: string | number
  fallback?: React.ReactNode
  fallbackText?: string
  fallbackBg?: string
}

export default function NextBaseIcon({
  sourcePending,
  circle,
  src,
  size,
  fallback,
  fallbackText,
  fallbackBg = defaultFallbackBg,
  ...restProps
}: NextBaseIconProps) {
  const [loadFailed, setLoadFailed] = useState(false)

  if (!src && sourcePending) {
    return (
      <Skeleton
        width={size}
        height={size}
        style={circle ? { borderRadius: 'var(--mantine-radius-circle)' } : undefined}
      />
    )
  }

  if (!src || loadFailed) {
    if (fallback) return fallback

    const text = fallbackText || '-'
    return (
      <Avatar
        size={size}
        color={fallbackBg}
        radius={circle ? undefined : 0}
        style={{ flexShrink: 0 }}
        name={text}
      />
    )
  }

  return (
    <Box
      w={size}
      h={size}
      pos="relative"
      style={{
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: circle ? 'var(--mantine-radius-circle)' : undefined,
      }}
    >
      <NextImage
        src={src}
        fill
        style={{
          objectFit: 'contain',
        }}
        placeholder={defaultPlaceholder}
        onError={() => setLoadFailed(true)}
        {...restProps}
      />
    </Box>
  )
}
